import React, { useEffect, useState } from "react";
import {
  Modal,
  Fade,
  Typography,
  Button,
  Grid,
  Tooltip,
  Container,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { CryptoState } from "../CryptoContext";
import LlamaImage from "../assets/Logo.webp";
import useWindowSize from "../utils/useWindowSize";
import { MdContentCopy } from "react-icons/md";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "500px",
    margin: "auto",
  },
  paper: {
    background: "linear-gradient(135deg, #FF0080 0%, #FF4DA6 100%)",
    border: "3px solid #FFFF00",
    boxShadow: "0 0 30px rgba(255, 0, 128, 0.5)",
    padding: theme.spacing(3, 4, 4),
    maxWidth: 400,
    textAlign: "center",
    color: "#ffffff",
    position: "relative",
    borderRadius: "20px",
    backdropFilter: "blur(10px)",
  },
  title: {
    background: "linear-gradient(135deg, #FFFF00 0%, #FFFF66 100%)",
    width: "auto",
    padding: theme.spacing(1),
    borderRadius: "15px",
    marginBottom: theme.spacing(2),
    color: "#000000",
    fontWeight: "bold",
    textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
    boxShadow: "0 0 20px rgba(255, 255, 0, 0.4)",
  },
  closeButton: {
    marginTop: "20px",
    background: "linear-gradient(135deg, #FFFF00 0%, #FFFF66 100%)",
    color: "#000000",
    fontWeight: "bold",
    borderRadius: "15px",
    border: "2px solid #FF0080",
    "&:hover": {
      background: "linear-gradient(135deg, #FFFF66 0%, #FFFF00 100%)",
      boxShadow: "0 0 25px rgba(255, 255, 0, 0.6)",
      transform: "scale(1.05)",
    },
  },
  llamaImage: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    position: "absolute",
    top: -65,
    left: "calc(50% - 50px)",
    border: "4px solid #FFFF00",
    boxShadow: "0 0 20px rgba(255, 255, 0, 0.5)",
    animation: "float 3s ease-in-out infinite",
  },
  contractAddress: {
    marginTop: theme.spacing(2),
  },
  dateContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, #FFFF00 0%, #FFFF66 100%)",
    color: "#000000",
    padding: theme.spacing(2),
    borderRadius: "15px",
    marginTop: theme.spacing(2),
    flexDirection: "column",
    border: "2px solid #FF0080",
    boxShadow: "0 0 20px rgba(255, 255, 0, 0.4)",
  },
  dateText: {
    fontWeight: "bold",
    fontSize: "1.4rem",
    textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
  },
  calendarButton: {
    marginTop: "10px",
  },
  emphasizedText: {
    fontWeight: 900,
    fontSize: "1.8rem",
    textAlign: "center",
    marginTop: theme.spacing(1),
    color: "#ffffff",
    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px)",
    },
    "50%": {
      transform: "translateY(-10px)",
    },
  },
}));

const WalletModal = () => {
  const { isMobile } = useWindowSize();
  const classes = useStyles();
  const { open, setOpen } = CryptoState();
  const contractAddress = "FYHr6YAACyTPuALnCTcgi8Pf1odXS21UzLBX1vNskSUQ";

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(contractAddress);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    setOpen(true);
  }, [setOpen]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Modal
      aria-labelledby="wallet-modal-title"
      aria-describedby="wallet-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Container className={classes.paper}>
          <img
            src={LlamaImage}
            alt="Retro Llama"
            className={classes.llamaImage}
          />
          <Typography
            variant="h5"
            id="wallet-modal-title"
            className={classes.title}
          >
            Presale is Live!
            <Button
              variant="outlined"
              className={classes.closeButton}
              onClick={handleClose}
              size="small"
              sx={{
                background: "linear-gradient(135deg, #FFFF00 0%, #FFFF66 100%)",
                color: "#000000",
                position: "absolute",
                width: 30,
                height: 30,
                top: 10,
                right: 10,
                borderRadius: "50%",
                border: "2px solid #FF0080",
                boxShadow: "0 0 15px rgba(255, 255, 0, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #FFFF66 0%, #FFFF00 100%)",
                  boxShadow: "0 0 25px rgba(255, 255, 0, 0.6)",
                  transform: "scale(1.1)",
                },
              }}
            >
              X
            </Button>
          </Typography>

          <Typography
            variant="subtitle2"
            id="wallet-modal-description"
            paragraph
            className={classes.emphasizedText}
          >
            Send 1 SOL to the following wallet address to receive 1,000,000
            LLAMA tokens.
          </Typography>
          <Grid item xs={12} sm={4} textAlign="center">
            <div className={classes.contractAddress}>
              <Tooltip title={contractAddress} arrow>
                <Button
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #FFFF00 0%, #FFFF66 100%)",
                    color: "#000000",
                    "&:hover": {
                      background: "linear-gradient(135deg, #FFFF66 0%, #FFFF00 100%)",
                      boxShadow: "0 0 25px rgba(255, 255, 0, 0.6)",
                      transform: "scale(1.05)",
                    },
                    marginLeft: isMobile ? 0 : 0,
                    marginTop: isMobile ? 0 : 0,
                    fontFamily: "Montserrat",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    borderRadius: "15px",
                    border: "2px solid #FF0080",
                    boxShadow: "0 0 15px rgba(255, 255, 0, 0.4)",
                  }}
                  onClick={handleCopy}
                >
                  Official Llama Presale Wallet <MdContentCopy />
                </Button>
              </Tooltip>
            </div>
          </Grid>

          <Typography
            variant="body1"
            id="wallet-modal-description"
            paragraph
            className={classes.emphasizedText}
          >
            Presale ends on: <br />
            November 4th 23:59 UTC
          </Typography>
          <Typography
            variant="subtitle2"
            id="wallet-modal-description"
            paragraph
            className={classes.emphasizedText}
          >
            300 SOL hardcap for the presale. 10 SOl minimum to launch or we will
            have to extend the launch.
          </Typography>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
          >
            <MuiAlert onClose={handleSnackbarClose} severity="success">
              Wallet address copied to clipboard!
            </MuiAlert>
          </Snackbar>
        </Container>
      </Fade>
    </Modal>
  );
};

export default WalletModal;
